import React, { useState, useEffect } from 'react';
import { ShoppingCart, Filter } from 'lucide-react';
import Footer from '../components/Footer';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const StorePage = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [cart, setCart] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/products`)
      .then(r => r.json())
      .then(data => setProducts(data.products || []))
      .catch(() => toast.error('Failed to load products.'))
      .finally(() => setLoading(false));
  }, []);

  const categories = ['All', ...new Set(products.map(p => p.category).filter(Boolean))];

  const filteredProducts = selectedCategory === 'All'
    ? products
    : products.filter(product => product.category === selectedCategory);

  const addToCart = (product) => {
    setCart(prev => [...prev, product]);
    toast.success(`${product.name} added to cart!`);
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-24 bg-bgn-black">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 text-center">
          <h1 className="text-5xl lg:text-7xl font-bold mb-8 text-white">
            BGN Store
          </h1>
          <p className="text-xl lg:text-2xl text-bgn-grey leading-relaxed max-w-4xl mx-auto">
            Rep the community with official BGN merchandise and gaming gear
          </p>
        </div>
      </section>

      {/* Category Filter & Cart */}
      <section className="py-8 bg-bgn-grey/5 border-b border-bgn-grey/20">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            <div className="flex flex-wrap gap-4">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
                    selectedCategory === category
                      ? 'bg-bgn-gold text-bgn-black'
                      : 'bg-bgn-grey/20 text-bgn-grey hover:bg-bgn-grey/30 hover:text-white'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
            
            <div className="flex items-center space-x-2 bg-bgn-grey/20 text-white px-4 py-2 rounded-full">
              <ShoppingCart size={18} />
              <span className="font-medium">{cart.length} items</span>
            </div>
          </div>
        </div>
      </section>

      {/* Products Grid */}
      <section className="py-24 bg-bgn-black">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          {loading ? (
            <div className="text-center py-16">
              <p className="text-xl text-bgn-grey">Loading products...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-xl text-bgn-grey">No products found in this category.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product) => (
                <div
                  key={product.id}
                  className="bg-bgn-grey/10 rounded-lg overflow-hidden hover:bg-bgn-grey/20 transition-all duration-200 hover:scale-105"
                >
                  <div className="aspect-square relative">
                    <img
                      src={product.image_url}
                      alt={product.name}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-bgn-purple text-white px-3 py-1 rounded-full text-sm font-bold">
                        {product.category}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6 space-y-4">
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">
                        {product.name}
                      </h3>
                      <p className="text-2xl font-bold text-bgn-gold">
                        {formatPrice(product.price)}
                      </p>
                    </div>
                    
                    {/* Size Options */}
                    {product.sizes && (
                      <div>
                        <p className="text-sm text-bgn-grey mb-2">Sizes:</p>
                        <div className="flex flex-wrap gap-2">
                          {product.sizes.map((size, index) => (
                            <span 
                              key={index}
                              className="bg-bgn-grey/20 text-white px-3 py-1 rounded text-sm"
                            >
                              {size}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Color Options */}
                    {product.colors && (
                      <div>
                        <p className="text-sm text-bgn-grey mb-2">Colors:</p>
                        <div className="flex flex-wrap gap-2">
                          {product.colors.map((color, index) => (
                            <span 
                              key={index}
                              className="bg-bgn-grey/20 text-white px-3 py-1 rounded text-sm"
                            >
                              {color}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    <button 
                      onClick={() => addToCart(product)}
                      className="w-full bg-bgn-gold text-bgn-black py-3 rounded-lg font-bold hover:bg-white transition-colors duration-200 flex items-center justify-center space-x-2"
                    >
                      <ShoppingCart size={18} />
                      <span>Add to Cart</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Coming Soon Section */}
      <section className="py-24 bg-bgn-purple/10">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold mb-8 text-white">
            More Coming Soon
          </h2>
          <p className="text-xl text-bgn-grey mb-12 max-w-2xl mx-auto">
            We're constantly adding new products. Sign up for updates on the latest BGN merchandise.
          </p>
          
          <div className="bg-bgn-black rounded-lg p-8 max-w-md mx-auto">
            <h3 className="text-2xl font-bold text-white mb-4">Product Updates</h3>
            <p className="text-bgn-grey mb-6">Be first to know about new releases</p>
            
            <div className="space-y-4">
              <input 
                type="email" 
                placeholder="Enter your email"
                className="w-full px-4 py-3 bg-bgn-grey/20 border border-bgn-grey/40 rounded-lg text-white placeholder-bgn-grey focus:outline-none focus:border-bgn-gold"
              />
              <button className="w-full bg-bgn-gold text-bgn-black py-3 rounded-lg font-bold hover:bg-white transition-colors duration-200">
                Notify Me
              </button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default StorePage;